import React from "react";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import { visuallyHidden } from "@mui/utils";
import { getComparator, stableSort } from "../../../_utils/muiTableUtils";
import { useMemo } from "react";
import { Collapse } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createContext } from "react";
import AlertModal from "../../_common/AlertModal";

export const QmsDataEditModalContext = createContext();
export const QmsDataExpandedDetailContext = createContext();

/**
 * Reusable data table component
 * @param {Object} props
 */
export function QmsDataTable(props) {
  const {
    data,
    columns,
    idName,
    editButtonCaption,
    editModalComponent,
    isExpandable,
    expandedDetailComponent,
    onDelete,
  } = props;

  const [modalState_edit, setModalState_edit] = useState({
    showModal: false,
    data: null,
  });
  const toggleEditModal = () =>
    setModalState_edit({ ...modalState_edit, showModal: !modalState_edit.showModal });

  const [showAlert_delete, _setShowAlert_delete] = useState(false);
  const toggleAlert_delete = () => _setShowAlert_delete(!showAlert_delete);

  const [searchString, setSearchString] = useState("");
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("calories");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Used for search
  const serializedRows = useMemo(() => {
    return (data || []).map((r) => {
      let rowString = "";
      for (const key in r) {
        if (key !== idName) rowString = `${rowString} ${r[key]}`.trim();
      }
      return rowString;
    });
  }, [data]);

  const rows = useMemo(() => {
    // Search filter: if searchString exists, check if searchString is part
    // of the serialized string of values for the row's data object.
    const updatedData = (data || [])
      .map((d) => ({ ...d }))
      .filter(
        (d) =>
          !searchString.trim() ||
          Object.values(d)
            .filter((v) => v !== d[idName])
            .join(" ")
            .toLowerCase()
            .includes(searchString.toLowerCase())
      );
    return stableSort(updatedData, getComparator(order, orderBy)).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [data, order, orderBy, page, rowsPerPage, searchString]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleAlert_delete = (action) => () => {
    if (action === "accept") {
      onDelete(selected);
      setSelected([]); // Clear selections
      setSearchString(""); // Clear search
    }
    toggleAlert_delete();
  };

  const handleClick_selectAll = (e) => {
    if (e.target.checked) {
      const newSelected = rows.map((n) => n[idName]);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleChange_search = (e) => {
    setSearchString(e.target.value);
  };

  const handleClick_update = (id) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const selectedData = data.find((d) => d[idName] === id);
    if (selectedData) setModalState_edit({ showModal: true, data: selectedData });
  };

  const selectItem = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - (data || []).length) : 0;

  return (
    <>
      <div className="d-flex flex-row w-100 mb-2">
        <div className="qms-filter-component qms-filter-component__input flex-grow-1 mr-2">
          <div className="input-group">
            <div className="input-group-prepend">
              <span className="input-group-text bg-lightgray">
                <FontAwesomeIcon icon="search" className="fa-fw text-muted" />
              </span>
            </div>
            <input
              name="filter"
              id="filter"
              type="search"
              className="form-control"
              value={searchString}
              onChange={handleChange_search}
              placeholder="Search..."
            />
          </div>
        </div>
        <div className="qms-filter-component qms-filter-component__btn">
          <button
            name="add"
            id="add"
            onClick={() =>
              setModalState_edit({ showModal: !modalState_edit.showModal, data: null })
            }
            className="btn btn-info"
          >
            <FontAwesomeIcon icon="plus" className="fa-fw mr-2" />
            Add...
          </button>
        </div>
        {selected.length > 0 && (
          <div className="qms-filter-component qms-filter-component__btn">
            <button
              name="delete"
              id="delete"
              onClick={() => onDelete && toggleAlert_delete()}
              className="btn btn-danger ml-2"
            >
              <FontAwesomeIcon icon="trash-alt" className="fa-fw mr-2" />
              Delete ({selected.length})
            </button>
          </div>
        )}
      </div>
      <Box sx={{ width: "100%" }}>
        <Paper elevation={0} variant="outlined" sx={{ width: "100%", mb: 2 }}>
          <TableContainer>
            <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="small">
              <QmsDataTableHead
                columns={columns}
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleClick_selectAll}
                onRequestSort={handleRequestSort}
                rowCount={rows.length}
                isExpandable={isExpandable}
              />
              <TableBody>
                {rows.map((row, index) => {
                  const isItemSelected = isSelected(row[idName]);
                  return (
                    <QmsDataTableRow
                      key={`qms-data-table-row_${index}`}
                      row={row}
                      index={index}
                      isItemSelected={isItemSelected}
                      selectItem={selectItem}
                      idName={idName}
                      columns={columns}
                      handleClick_update={handleClick_update}
                      editButtonCaption={editButtonCaption || "Update..."}
                      isExpandable={isExpandable}
                      expandedDetailComponent={expandedDetailComponent}
                    />
                  );
                })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 43 * emptyRows }}>
                    <TableCell
                      colSpan={(columns || []).length + (isExpandable ? 3 : 2)}
                    />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={(data || []).length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              "& .MuiTablePagination-selectLabel": {
                my: "auto",
              },
              "& .MuiTablePagination-displayedRows": {
                my: "auto",
              },
            }}
          />
        </Paper>
      </Box>
      {modalState_edit.showModal && (
        <QmsDataEditModalContext.Provider
          value={{ modalState: modalState_edit, toggleModal: () => toggleEditModal() }}
        >
          {editModalComponent}
        </QmsDataEditModalContext.Provider>
      )}
      {showAlert_delete && (
        <AlertModal
          alertTitle="Confirm Delete"
          alertTitleClass="bg-danger text-white"
          alertTitleIcon="exclamation-triangle"
          alertText="Are you sure you want to delete the selected item(s)?"
          acceptCaption="Yes"
          acceptClass="danger"
          provideReject={true}
          rejectCaption="No"
          rejectClass="secondary"
          handleAlertAction={handleAlert_delete}
          showAlertModal={showAlert_delete}
          toggleAlertModal={toggleAlert_delete}
        />
      )}
    </>
  );
}

function QmsDataTableHead(props) {
  const {
    columns,
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    isExpandable,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {isExpandable && <TableCell />}
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              "aria-label": "select all",
            }}
          />
        </TableCell>
        {columns.map((column) => (
          <TableCell
            key={column.id}
            align={column.numeric ? "right" : "left"}
            padding={column.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === column.id ? order : false}
            sx={{ fontWeight: "fontWeightBold" }}
          >
            <TableSortLabel
              active={orderBy === column.id}
              direction={orderBy === column.id ? order : "asc"}
              onClick={createSortHandler(column.id)}
            >
              {column.label}
              {orderBy === column.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell />
      </TableRow>
    </TableHead>
  );
}

QmsDataTableHead.propTypes = {
  columns: PropTypes.array.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
  isExpandable: PropTypes.bool.isRequired,
};

function QmsDataTableRow(props) {
  const {
    row,
    index: rowIndex,
    isItemSelected,
    selectItem,
    columns,
    handleClick_update,
    idName,
    editButtonCaption,
    isExpandable,
    expandedDetailComponent,
  } = props;

  const [isOpen, setOpen] = useState(false);
  const toggleOpen = () => setOpen(!isOpen);

  const labelId = `qms-data-table-checkbox-${rowIndex}`;

  const handleChange_check = (id) => (e) => selectItem(id);

  const handleClick_row = (id) => (e) => {
    e.preventDefault();
    selectItem(id);
  };

  const handleClick_open = (e) => {
    e.stopPropagation();
    toggleOpen();
  };

  return (
    <>
      <TableRow
        hover
        onClick={handleClick_row(row[idName])}
        tabIndex={-1}
        selected={isOpen}
        sx={{ "& > *": { borderBottom: "unset" } }}
      >
        {isExpandable && (
          <TableCell sx={{ width: 10 }}>
            <IconButton aria-label="expand row" size="small" onClick={handleClick_open}>
              {isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
            </IconButton>
          </TableCell>
        )}
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            checked={isItemSelected}
            onChange={handleChange_check(row[idName])}
            inputProps={{
              "aria-labelledby": labelId,
            }}
          />
        </TableCell>
        {(columns || []).map((column, colIndex) => {
          const key = `qms-data-table-cell-${rowIndex}-${colIndex}`;
          if (colIndex === 0)
            return (
              <TableCell
                key={key}
                component="th"
                id={labelId}
                scope="row"
                align={column.numeric ? "right" : "left"}
                padding="none"
              >
                {column.render ? column.render(row) : row[column.id]}
              </TableCell>
            );
          else
            return (
              <TableCell key={key} align={column.numeric ? "right" : "left"}>
                {column.render ? column.render(row) : row[column.id]}
              </TableCell>
            );
        })}
        <TableCell
          key={`qms-data-table-cell-${rowIndex}-edit`}
          align="right"
          onClick={handleClick_update(row[idName])}
          sx={{ paddingLeft: "2px", paddingRight: "2px", minWidth: "8em" }}
        >
          <button id="edit" className="btn btn-sm btn-secondary">
            <FontAwesomeIcon icon="edit" className="fa-fw mr-2" />
            {editButtonCaption}
          </button>
        </TableCell>
      </TableRow>
      {isExpandable && (
        <TableRow>
          <TableCell
            style={{
              paddingBottom: 0,
              paddingTop: 0,
              borderBottom: isOpen ? 1 : "none",
            }}
            colSpan={(columns || []).length + 3}
          >
            <Collapse in={isOpen} unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <QmsDataExpandedDetailContext.Provider value={{ data: row }}>
                  {expandedDetailComponent}
                </QmsDataExpandedDetailContext.Provider>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
